/* eslint-disable @typescript-eslint/ban-ts-comment */
import type { ContentFragment } from '@oneaudi/falcon-tools';
import type { TextButtonProps } from '@oneaudi/unified-web-components';
import type {
  Alignment,
  ButtonElement,
  ButtonElementContent,
  ButtonsContent,
  OpenIn,
  TextTwoLayout,
  ThemeColor,
  TextLinkElementContent,
} from '../../types';

export interface EditorJsonContent extends ButtonsContent {
  __type?: 'editor.json';
}

export interface FalconContent extends Partial<ContentFragment> {
  __type: 'aem-headless';
  fields: {
    buttons: [];
    paddingTop: boolean;
    alignment: Alignment;
    themeColor: ThemeColor;
    buttonCombinations: string[] | number[];
  };
}
export interface ButtonElementFalcon {
  fields: {
    title: string;
    firstVariant?: TextButtonProps['variant'] | 'standalone' | 'text';
    firstLabel: string;
    firstUrl: string;
    textTwoLayout?: TextTwoLayout | 'horizontal';
    firstAriaLabel?: string;
    firstOpenIn?: OpenIn | 'same';
    secondVariant?: TextButtonProps['variant'] | 'standalone' | 'text';
    secondLabel: string | null;
    secondUrl: string | null;
    secondAriaLabel?: string;
    secondOpenIn?: OpenIn | 'same';
    thirdVariant?: TextButtonProps['variant'] | 'standalone' | 'text';
    thirdLabel: string | null;
    thirdUrl: string | null;
    thirdAriaLabel?: string;
    thirdOpenIn?: OpenIn | 'same';
  };
}

function normalizeButtonElement(buttons: ButtonElementFalcon[]): ButtonElement[] {
  if (!buttons) {
    return [];
  }

  return buttons.map((button: ButtonElementFalcon) => {
    const { fields } = button;
    const {
      title,
      firstVariant,
      firstLabel,
      firstAriaLabel,
      firstOpenIn,
      textTwoLayout,
      firstUrl,
      secondVariant,
      secondLabel,
      secondAriaLabel,
      secondOpenIn,
      secondUrl,
      thirdVariant,
      thirdLabel,
      thirdAriaLabel,
      thirdOpenIn,
      thirdUrl,
    } = fields;

    const buttonContent: ButtonElementContent[] | TextLinkElementContent[] = [];
    if (firstLabel && firstUrl) {
      buttonContent.push({
        // @ts-expect-error
        variant: firstVariant,
        label: firstLabel,
        ariaLabel: firstAriaLabel,
        openIn: firstOpenIn,
        url: firstUrl,
      });
    }
    if (secondLabel && secondUrl) {
      buttonContent.push({
        // @ts-expect-error
        variant: secondVariant,
        label: secondLabel,
        ariaLabel: secondAriaLabel,
        openIn: secondOpenIn,
        url: secondUrl,
      });
    }
    if (thirdLabel && thirdUrl) {
      buttonContent.push({
        // @ts-expect-error
        variant: thirdVariant,
        label: thirdLabel,
        ariaLabel: thirdAriaLabel,
        openIn: thirdOpenIn,
        url: thirdUrl,
      });
    }
    return {
      title,
      buttonContent,
      textTwoLayout,
    };
  });
}

export function mapContent(
  content: FalconContent | EditorJsonContent | undefined,
): EditorJsonContent {
  if (!content || Object.keys(content).length === 0) {
    console.warn('No Editor content found for Buttons FA');
    return {} as EditorJsonContent;
  }
  if (content.__type !== 'aem-headless') {
    return content;
  }
  return {
    __type: 'editor.json',
    buttonCombinations: content.fields.buttonCombinations,
    buttons: normalizeButtonElement(content.fields.buttons),
    paddingTop: content.fields.paddingTop,
    alignment: content.fields.alignment,
    themeColor: content.fields.themeColor,
  };
}
